import { useState } from "react";
import { useLocation } from "react-router-dom";
import useAppointmentsStore from "./customerAppointmentsStore";
import { cancelCustomerAppointment } from "../../services/CustomerPortal";
import { formatPrice } from "../../utils/pricing";
import styles from "./appointments.module.css";

function formatDate(value) {
  const [year, month, day] = String(value ?? "").split("-").map(Number);
  if (!year || !month || !day) return value || "-";
  return `${String(month).padStart(2, "0")}/${String(day).padStart(2, "0")}/${year}`;
}

function formatTime(value) {
  const [hours, minutes] = String(value ?? "").split(":").map(Number);
  if (Number.isNaN(hours) || Number.isNaN(minutes)) return value || "-";
  return new Date(2000, 0, 1, hours, minutes).toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
  });
}

function formatStatus(value) {
  return String(value || "-").replace(/_/g, " ");
}

const statusStyles = {
  confirmed: styles.badgeSuccess,
  completed: styles.badgeSuccess,
  pending: styles.badgeWarning,
  cancelled: styles.badgeDanger,
};

const CANCEL_CUTOFF_MS = 24 * 60 * 60 * 1000;

// date/start_time come back as wall-clock strings, so build the start in the browser's local time.
function canCancel(row, now) {
  if (String(row.status ?? "").toLowerCase() === "cancelled") return false;

  const [year, month, day] = String(row.date ?? "").split("-").map(Number);
  const [hours, minutes] = String(row.start_time ?? "").split(":").map(Number);
  if (!year || !month || !day || Number.isNaN(hours) || Number.isNaN(minutes)) return false;

  const startsAt = new Date(year, month - 1, day, hours, minutes).getTime();
  return startsAt - now > CANCEL_CUTOFF_MS;
}

function CustomerPortalAppointments({ appointments: propAppointments }) {
  const location = useLocation();
  const storeAppointments = useAppointmentsStore((state) => state.appointments);
  const setAppointments = useAppointmentsStore((state) => state.setAppointments);
  const [cancelledIds, setCancelledIds] = useState(() => new Set());
  const [isCancelling, setIsCancelling] = useState(false);
  const [cancelError, setCancelError] = useState("");

  const routeAppointments = location.state?.appointments;
  const appointments = propAppointments ?? routeAppointments ?? storeAppointments ?? [];
  const list = Array.isArray(appointments) ? appointments : appointments?.appointments ?? [];
  // Route state is a snapshot, so overlay cancellations made on this page.
  const rows = list
    .map((row) => (cancelledIds.has(row.id) ? { ...row, status: "cancelled" } : row))
    .sort((a, b) => `${b.date} ${b.start_time}`.localeCompare(`${a.date} ${a.start_time}`));
  const now = Date.now();

  const handleCancel = async (appointment) => {
    if (isCancelling) return;
    const confirmed = window.confirm(
      `Are you sure you want to cancel your ${appointment.pricing?.name || "appointment"} on ${formatDate(appointment.date)} at ${formatTime(appointment.start_time)}? This can't be undone.`
    );
    if (!confirmed) return;

    setIsCancelling(true);
    setCancelError("");
    try {
      await cancelCustomerAppointment(appointment.id);
      setCancelledIds((prev) => new Set(prev).add(appointment.id));
      setAppointments(
        storeAppointments.map((row) =>
          row.id === appointment.id ? { ...row, status: "cancelled" } : row
        )
      );
    } catch (error) {
      setCancelError(error.message || "Unable to cancel appointment");
    } finally {
      setIsCancelling(false);
    }
  };

  return (
    <div>
      {rows.length === 0 ? (
        <p className={styles.empty}>You don't have any appointments yet.</p>
      ) : (
        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th scope="col">Service</th>
                <th scope="col">Date</th>
                <th scope="col">Time</th>
                <th scope="col">Status</th>
                <th scope="col">Total</th>
                <th scope="col"><span className={styles.srOnly}>Actions</span></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id}>
                  <td className={styles.service} data-label="Service">
                    {row.pricing?.name || "Appointment"}
                  </td>
                  <td data-label="Date">{formatDate(row.date)}</td>
                  <td data-label="Time">
                    {formatTime(row.start_time)} – {formatTime(row.end_time)}
                  </td>
                  <td className={styles.status} data-label="Status">
                    <span className={`${styles.badge} ${statusStyles[String(row.status ?? "").toLowerCase()] || ""}`}>
                      {formatStatus(row.status)}
                    </span>
                  </td>
                  <td className={styles.total} data-label="Total">
                    {row.pricing ? formatPrice(row.pricing.total_price) : "-"}
                  </td>
                  <td className={styles.actions}>
                    {canCancel(row, now) && (
                      <button
                        type="button"
                        className={styles.cancelButton}
                        onClick={() => handleCancel(row)}
                        disabled={isCancelling}
                      >
                        Cancel
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {cancelError && <p className={styles.cancelError} role="alert">{cancelError}</p>}
    </div>
  );
}

export default CustomerPortalAppointments;